/**
 * 185 (the owner: "armor as a thing, body armor, helmet, damage reduction for the armor and some life and expiration on
 * damage for the armor itself"): the pieces a unit wears — a helmet over the head, a vest (light or heavy) over the body
 * — each stopping a share of a hit by what it is (179's cartridge, or a blast: ARMOUR.stops) out of its own life, worn by
 * what it stops and spent at 0 (still worn: drawn, stopping nothing). A hit on the limbs meets none. Issued at the start
 * and whole again at a respawn (185's draw-down from kit is later). Pure.
 */
import type { ArmourIssue, ArmourPiece, ArmourWorn, CartridgeKind, HitLocation, PlayerState } from '../types';
import { ARMOUR } from '../constants';

/** The pieces an issue makes, whole (a helmet 'heavy' the heavy one; any other helmet — 'light', or a stored `true` — the light one). */
export function armourOf(issue: ArmourIssue | undefined): ArmourWorn | undefined {
  if (!issue || (!issue.helmet && !issue.vest)) return undefined;
  const piece = (kind: ArmourPiece['kind']): ArmourPiece => ({ kind, life: ARMOUR.life[kind], max: ARMOUR.life[kind] });
  return { ...(issue.helmet ? { helmet: piece(issue.helmet === 'heavy' ? 'heavyHelmet' : 'helmet') } : {}), ...(issue.vest ? { vest: piece(issue.vest) } : {}) };
}

/** What a unit's armour was issued as (its kinds, whatever their wear) — what a respawn gives back whole. */
export function issueOf(worn: ArmourWorn | undefined): ArmourIssue | undefined {
  if (!worn) return undefined;
  const vest = worn.vest?.kind === 'light' || worn.vest?.kind === 'heavy' ? worn.vest.kind : undefined;
  return { ...(worn.helmet ? { helmet: worn.helmet.kind === 'heavyHelmet' ? 'heavy' as const : 'light' as const } : {}), ...(vest ? { vest } : {}) };
}

/**
 * 190: the armour a choice issues — the Lab's (a side's: `helmets`, `heavyHelmets`; your own: `helmet`, `heavyHelmet`), the
 * loadout builder's and a squad type's text (its tokens, lower-cased): a helmet alone, light or heavy; a vest (light or heavy)
 * with the light helmet; `heavyKit` the heavy vest with the heavy helmet. `none` (or anything else): none.
 */
export const ARMOUR_CHOICES = ['none', 'helmet', 'heavyHelmet', 'light', 'heavy', 'heavyKit'] as const;
export type ArmourChoice = typeof ARMOUR_CHOICES[number];
export function armourIssueOf(choice: string | undefined | null): ArmourIssue | undefined {
  switch ((choice ?? '').toLowerCase()) {
    case 'helmet': case 'helmets': return { helmet: 'light' };
    case 'heavyhelmet': case 'heavyhelmets': return { helmet: 'heavy' };
    case 'light': return { helmet: 'light', vest: 'light' };
    case 'heavy': return { helmet: 'light', vest: 'heavy' };
    case 'heavykit': return { helmet: 'heavy', vest: 'heavy' };
    default: return undefined;
  }
}
/** The choice an issue is (armourIssueOf's inverse; a light vest under a heavy helmet, never a choice, reads as the vest). */
export function armourChoiceOf(issue: ArmourIssue | undefined): ArmourChoice {
  if (!issue) return 'none';
  const heavyHelmet = issue.helmet === 'heavy';
  if (issue.vest === 'heavy') return heavyHelmet ? 'heavyKit' : 'heavy';
  if (issue.vest === 'light') return 'light';
  return issue.helmet ? (heavyHelmet ? 'heavyHelmet' : 'helmet') : 'none';
}

/**
 * A hit of `amount` at `where` (a location, or a blast) by `round`: what gets through, and the unit with the piece that
 * took it worn by what it stopped. The head meets the helmet, the body the vest; the limbs none. A blast (a burst or, §5.21
 * step 8 — the owner: "sure vest interacts with explosions" — a shell) meets the vest, its blast share of the whole, and the
 * helmet, its blast share of the head's part (ARMOUR.blastHead), each out of its own life.
 */
export function throughArmour(unit: PlayerState, where: HitLocation | 'blast', round: CartridgeKind | undefined, amount: number): { amount: number; unit: PlayerState } {
  if (where === 'blast') return blastThroughArmour(unit, amount);
  const worn = unit.armour;
  const slot: keyof ArmourWorn | null = where === 'head' ? 'helmet' : where === 'body' ? 'vest' : null;
  const piece = slot ? worn?.[slot] : undefined;
  if (!worn || !slot || !piece || piece.life <= 0 || amount <= 0) return { amount, unit };
  const share = round ? ARMOUR.stops[piece.kind][round] : 0;
  const stopped = Math.min(amount * share, piece.life);
  if (stopped <= 0) return { amount, unit };
  return { amount: amount - stopped, unit: { ...unit, armour: { ...worn, [slot]: { ...piece, life: piece.life - stopped } } } };
}

/** A blast of `amount` through what a unit wears: the vest's share of it, then the helmet's of the head's part (65.35 step 8). */
function blastThroughArmour(unit: PlayerState, amount: number): { amount: number; unit: PlayerState } {
  const worn = unit.armour;
  if (!worn || amount <= 0) return { amount, unit };
  let left = amount, out = worn;
  const vest = worn.vest;
  if (vest && vest.life > 0) {
    const stopped = Math.min(amount * ARMOUR.stops[vest.kind].blast, vest.life);
    if (stopped > 0) { left = amount - stopped; out = { ...out, vest: { ...vest, life: vest.life - stopped } }; }
  }
  const helmet = worn.helmet;
  if (helmet && helmet.life > 0) {
    const stopped = Math.min(amount * ARMOUR.blastHead * ARMOUR.stops[helmet.kind].blast, helmet.life, left);
    if (stopped > 0) { left = left - stopped; out = { ...out, helmet: { ...helmet, life: helmet.life - stopped } }; }
  }
  return out === worn ? { amount, unit } : { amount: left, unit: { ...unit, armour: out } };
}

/** The walk's carry a heavy vest adds, and (190) a heavy helmet (× each other and the unit's own: the LMG's). */
export function armourCarry(issue: ArmourIssue | undefined): number {
  return (issue?.vest === 'heavy' ? ARMOUR.heavyCarry : 1) * (issue?.helmet === 'heavy' ? ARMOUR.heavyHelmetCarry : 1);
}
